import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { Search, MessageSquare, Clock } from "lucide-react";
import { cn } from "../lib/utils";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { dagVanIso } from "@/lib/datum";


export default function SearchDialog({ open, onOpenChange, onSelectConversation }) {
  const { t, taal } = useTaal();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  useEffect(() => {
    const searchConversations = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await axios.get(`${API}/conversations/search?q=${encodeURIComponent(query)}`);
        setResults(response.data.results || []);
      } catch (error) {
        console.error("Error searching:", error);
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchConversations, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={i} className="bg-[hsl(var(--accent))]/30 text-foreground rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] p-0 gap-0" data-testid="search-dialog">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="font-serif text-lg">{t("Gesprekken zoeken")}</DialogTitle>
        </DialogHeader>
        
        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("Berichten en gesprekken zoeken... (⌘K)")}
              className="pl-10"
              autoFocus
              data-testid="search-input"
            />
          </div>
        </div>

        {/* `[&>div>div]:!block`, en zonder die klasse doet de `truncate` op de
            titel hieronder niets.

            Radix rendert het kind van zijn scroll-viewport als `display: table`
            met `min-width: 100%`, en een tabel krimpt naar zijn INHOUD in plaats
            van zijn container te gehoorzamen. Gemeten op 24 augustus 2026 in een
            dialoog van 390 px breed, met een lange gesprekstitel:

              zonder : inhoudsdiv 916, titel 848 breed, rechterrand op 897
              met    : de titel wordt afgekapt binnen de dialoog

            De titel liep dus ruim 500 px buiten de dialoog. En `truncate` kapte
            wel degelijk af, alleen op zijn eigen doos van 848; die was zelf te
            breed. Franks woorden: op zijn telefoon onbruikbaar.

            DIT IS DE DERDE PLEK MET DEZELFDE FOUT. `Sidebar.jsx` kreeg deze
            reparatie op 26 juli 2026, `ChatArea.jsx` op 24 augustus, en dit
            scherm dezelfde dag nadat Frank vroeg of er nog meer waren. Er is nu
            een wachter die telt: `meting/opmaak.mjs` eist dat ELKE ScrollArea in
            de app hem draagt, zodat een vierde niet stil kan ontstaan. */}
        <ScrollArea className="max-h-[400px] [&>div>div]:!block">
          <div className="px-2 pb-4">
            {isSearching ? (
              <div className="text-center py-8 text-muted-foreground">
                {t("Zoeken...")}
              </div>
            ) : results.length > 0 ? (
              <div className="space-y-1">
                {results.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => onSelectConversation(result.id)}
                    className={cn(
                      "w-full text-left px-3 py-3 rounded-lg",
                      "hover:bg-[hsl(var(--muted))] transition-colors",
                      "focus:outline-none focus:bg-[hsl(var(--muted))]"
                    )}
                    data-testid={`search-result-${result.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <MessageSquare className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {highlightMatch(result.title, query)}
                        </p>
                        {result.snippet && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {highlightMatch(result.snippet, query)}
                          </p>
                        )}
                        <div className="flex items-center gap-1 mt-1.5 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {dagVanIso(result.updated_at, taal)}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : query.length >= 2 ? (
              <div className="text-center py-8 text-muted-foreground">
                {t("Geen resultaten voor \"{vraag}\"", { vraag: query })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>{t("Typ minstens 2 tekens om te zoeken")}</p>
                <p className="text-xs mt-2">
                  {t("Tip: gebruik")} <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">⌘K</kbd> {t("om zoeken altijd te openen")}
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
